// ─── Sdílené utility a vizuální mapy pro receptář (Polotovary / Produkty) ───

export const SECTION_COLOR = '#F59E0B';
export const SECTION_BG = 'rgba(245,158,11,0.12)';

// ── Datum / čas ──────────────────────────────────────────
export const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// Sekundy → M min / H:MM h (lidsky, pro meta řádek)
export const fmtTrvani = (seconds) => {
    if (!seconds) return null;
    const totalMin = Math.round(seconds / 60);
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m ? `${h}:${String(m).padStart(2, '0')} h` : `${h} h`;
};

// Cena → "1 234,56 Kč" (mezera tisíce, desetinná čárka, vždy jednotka)
const czNum = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtCena = (v) => {
    if (v == null || isNaN(v)) return null;
    return `${czNum.format(Number(v))} Kč`;
};
// Jen číslo v českém formátu (bez jednotky)
export const fmtCislo = (v) => (v == null || isNaN(v)) ? '0,00' : czNum.format(Number(v));

// ── Revize je zastaralá, pokud je starší než `months` měsíců ──
export const REVIZE_STALE_MONTHS = 6;
export const isRevizeStale = (v, months = REVIZE_STALE_MONTHS) => {
    if (!v) return false;
    const d = new Date(v);
    const hranice = new Date();
    hranice.setMonth(hranice.getMonth() - months);
    return d < hranice;
};

// ── Validační stav receptury (třístupňový výkřičník) ──
// red > orange > green — severity žebříček, počítáno na backendu (rec.validace).
export const VALIDACE_ORDER = ['red', 'orange', 'green'];
export const VALIDACE_COLOR = {
    red: '#EF4444',    // chybí povinná data
    orange: '#F97316', // revize chybí / starší 6 měsíců
    green: '#22C55E',  // jen tip od L(a)M(i)
};
export const VALIDACE_LABEL = {
    red: 'Chybí povinná data',
    orange: 'Revize',
    green: 'Tip od L(a)M(i)',
};
// Seznam všech hlášení daného stavu (pro tooltip).
export const validaceIssues = (v) => v
    ? [...(v.chyby || []), ...(v.varovani || []), ...(v.tipy || [])]
    : [];
// Kumulace stavů napříč položkami: { red, orange, green }
export const aggregateValidace = (items) => {
    const c = { red: 0, orange: 0, green: 0 };
    for (const it of items || []) {
        const s = it?.validace?.stav;
        if (s && c[s] != null) c[s]++;
    }
    return c;
};

// ── Strom kategorií ──────────────────────────────────────
export const flattenKatTree = (nodes, depth = 0) => {
    let result = [];
    for (const node of nodes || []) {
        result.push({ ...node, _depth: depth });
        if (node.children?.length) result = result.concat(flattenKatTree(node.children, depth + 1));
    }
    return result;
};

// Vrátí id uzlu + všech jeho potomků (pro počítání/filtry napříč podstromem)
export const getDescendantIds = (nodes, targetId) => {
    const ids = [];
    const collect = (items) => { for (const item of items || []) { ids.push(item.id); if (item.children?.length) collect(item.children); } };
    const find = (items) => {
        for (const item of items || []) {
            if (item.id == targetId) { ids.push(item.id); collect(item.children || []); return true; }
            if (item.children?.length && find(item.children)) return true;
        }
        return false;
    };
    find(nodes);
    return ids;
};

// Klíč skupiny variant — jeden řádek na (kategorie, název)
export const recVariantKey = (r) => `${r.kategorie_id || '_'}::${r.nazev}`;

// ── Vizuál kategorie: ikona (klíč) + barva odvozená z názvu ──
// Klíč se překládá na Lucide ikonu v <RecipeKatIcon>.
const KAT_KEYWORDS = [
    [/(omáčk|omack|dip|salsa|pesto)/i, 'soup'],
    [/(těst|test|pizza|chléb|chleb|pečiv|peciv|rohlík|rohlik|houska)/i, 'wheat'],
    [/(mas|hověz|hovez|vepř|vepr|kuř|kur|drůbe|drube|steak|burger)/i, 'beef'],
    [/(ryb|losos|tuňák|tunak|krevet|moř|mor)/i, 'fish'],
    [/(mlék|mlek|sýr|syr|smetan|jogurt|máslo|maslo)/i, 'milk'],
    [/(salát|salat|zelenin|listov)/i, 'salad'],
    [/(marin|nálev|nalev|dresink|zálivk|zalivk)/i, 'droplet'],
    [/(sladk|dezert|krém|krem|čokolád|cokolad|cukr|sirup|poleva|glazur)/i, 'candy'],
    [/(nápoj|napoj|káv|kav|čaj|caj|džus|dzus|limonád|limonad)/i, 'coffee'],
    [/(polév|polev|vývar|vyvar|bujón|bujon)/i, 'cookingpot'],
    [/(sendvič|sendvic|wrap|tortill|bageta|panini)/i, 'sandwich'],
    [/(příloh|priloh|rýže|ryze|brambor|hranolk|těstovin|testovin)/i, 'utensils'],
    [/(koření|koreni|směs|smes)/i, 'package'],
];

export const KAT_PALETTE = [
    '#F59E0B', '#EF4444', '#06B6D4', '#A855F7', '#10B981',
    '#3B82F6', '#EC4899', '#EAB308', '#F97316', '#8B5CF6', '#14B8A6', '#64748B',
];

const hashStr = (s) => {
    let h = 0;
    for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h;
};

// Vizuál kategorie — použije uloženou barvu, jinak odvodí z názvu.
export const getKatVisual = (nazev, barva) => {
    const n = nazev || '';
    let iconKey = 'package';
    for (const [re, key] of KAT_KEYWORDS) { if (re.test(n)) { iconKey = key; break; } }
    const color = barva || KAT_PALETTE[hashStr(n) % KAT_PALETTE.length];
    return { iconKey, color };
};
